import { createRoutine } from 'redux-saga-routines'
import { put, call, select, takeLatest, delay, fork } from '@redux-saga/core/effects'
import { toast } from 'react-toastify'
import db from 'services/offlineDb'
import * as taskService from 'services/taskService'
import {
  getPendingSyncEntries,
  updateSyncEntryStatus,
  clearCompletedSyncEntries,
  replaceTempId,
  cleanupOrphanedFiles
} from 'services/offlineSync'
import { getTasksRoutine, getProjectsRoutine, updateTaskRoutine } from 'features/taskManagement/ducks/actions'

export const setOnlineStatusRoutine = createRoutine('SET_ONLINE_STATUS')
export const updateSyncCountsRoutine = createRoutine('UPDATE_SYNC_COUNTS')
export const triggerSyncRoutine = createRoutine('TRIGGER_SYNC')
export const syncProgressRoutine = createRoutine('SYNC_PROGRESS')
export const syncCompleteRoutine = createRoutine('SYNC_COMPLETE')
export const syncErrorRoutine = createRoutine('SYNC_ERROR')
export const clearSyncErrorsRoutine = createRoutine('CLEAR_SYNC_ERRORS')
export const syncFailRoutine = createRoutine('SYNC_FAIL')

// Helper: check online status — uses Redux store (more reliable than navigator.onLine on mobile)
export const isOnline = () => {
  const store = window.reduxStore
  if (store) return store.getState().offline?.isOnline ?? navigator.onLine
  return navigator.onLine
}

// Refresh sync queue counts from IndexedDB
function* refreshSyncCounts() {
  try {
    const queueCount = yield call(() => db.syncQueue.where('status').anyOf('pending', 'syncing').count())
    const fileCount = yield call(() => db.pendingFiles.where('status').anyOf('pending', 'syncing').count())
    // Sum blob sizes via cursor to avoid loading all blobs into memory
    let totalSize = 0
    yield call(() => db.pendingFiles.where('status').anyOf('pending', 'syncing').each(f => {
      totalSize += f.blob?.size || 0
    }))
    yield put(updateSyncCountsRoutine.success({
      syncQueueCount: queueCount,
      pendingFileCount: fileCount,
      pendingFileSizeMb: Math.round(totalSize / 1024 / 1024 * 10) / 10
    }))
  } catch (e) {
    console.error('Failed to refresh sync counts:', e)
  }
}

function* handleOnlineStatusChange({ payload }) {
  yield put(setOnlineStatusRoutine.success(payload))
  if (payload.isOnline) {
    yield put(triggerSyncRoutine())
  }
}

// --- Sync Queue Processor ---

function* processSyncQueue() {
  const entries = yield call(getPendingSyncEntries)
  if (entries.length === 0) return

  yield put(syncProgressRoutine.success({ current: 0, total: entries.length, currentItem: 'Starting sync...' }))

  let successCount = 0
  let failCount = 0

  let interrupted = false
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]

    if (!isOnline()) {
      toast.warning('Lost connection during sync. Will resume when back online.')
      interrupted = true
      break
    }

    yield put(syncProgressRoutine.success({
      current: i + 1,
      total: entries.length,
      currentItem: `Syncing ${entry.type.toLowerCase().replace(/_/g, ' ')}...`
    }))

    try {
      yield call(updateSyncEntryStatus, entry.id, 'syncing')
      yield call(processSingleSyncEntry, entry)
      yield call(updateSyncEntryStatus, entry.id, 'completed')
      successCount++
    } catch (error) {
      const status = error?.response?.status

      if (status === 409) {
        yield call(updateSyncEntryStatus, entry.id, 'failed', 'Conflict: data was modified by someone else')
        yield put(syncErrorRoutine.success({
          entryId: entry.id,
          type: entry.type,
          error: 'Conflict detected. Please review and re-submit.'
        }))
        failCount++
      } else if (status >= 500) {
        yield call(updateSyncEntryStatus, entry.id, 'pending', error.message)
        failCount++
      } else {
        yield call(updateSyncEntryStatus, entry.id, 'failed', error.message || 'Unknown error')
        failCount++
      }

      // Mark associated pendingFile as failed too (so panel shows it)
      if (entry.type === 'UPLOAD_FILE' && entry.payload?.offlineFileId && status !== 500) {
        try {
          const pf = yield call(() => db.pendingFiles.where('offlineFileId').equals(entry.payload.offlineFileId).first())
          if (pf) {
            yield call(() => db.pendingFiles.update(pf.id, { status: 'failed', lastError: error.message || 'Upload failed' }))
          }
        } catch (e) { /* ignore */ }
      }
    }
  }

  yield call(clearCompletedSyncEntries)

  if (!interrupted) {
    yield put(syncCompleteRoutine.success())
  } else {
    // Partial sync — just stop the spinner, don't reset counts
    yield put(syncFailRoutine.success())
  }

  // Always refresh counts from IDB (source of truth)
  yield put(updateSyncCountsRoutine.trigger())

  if (successCount > 0) {
    yield put(getTasksRoutine.trigger())
    yield put(getProjectsRoutine.trigger())
  }

  if (successCount > 0 && failCount === 0 && !interrupted) {
    toast.success(`Synced ${successCount} change${successCount !== 1 ? 's' : ''} successfully`)
  } else if (successCount > 0 && failCount > 0) {
    toast.warning(`Synced ${successCount}, ${failCount} failed. Check sync status.`)
  } else if (failCount > 0) {
    toast.error(`Sync failed for ${failCount} change${failCount !== 1 ? 's' : ''}`)
  }
}

function* processSingleSyncEntry(entry) {
  const { type, payload, tempId } = entry

  switch (type) {
    case 'CREATE_TASK': {
      const result = yield call(taskService.createTask, payload)
      const realId = result.data.data.id
      if (tempId) {
        yield call(replaceTempId, tempId, realId)
      }
      yield call(() => db.tasks.put(result.data.data))
      break
    }
    case 'UPDATE_TASK': {
      const result = yield call(taskService.updateTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      break
    }
    case 'DELETE_TASK': {
      yield call(taskService.deleteTask, payload)
      yield call(() => db.tasks.delete(payload))
      yield call(cleanupOrphanedFiles, payload)
      break
    }
    case 'SUBMIT_TASK': {
      const result = yield call(taskService.submitTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      break
    }
    case 'APPROVE_TASK': {
      const result = yield call(taskService.approveTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      break
    }
    case 'REJECT_TASK': {
      const result = yield call(taskService.rejectTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      break
    }
    case 'HOLD_TASK': {
      const result = yield call(taskService.holdTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      break
    }
    case 'RESUME_TASK': {
      const result = yield call(taskService.resumeTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      break
    }
    case 'SIGN_OFF_TASK': {
      const result = yield call(taskService.signOffTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      break
    }
    case 'SAVE_FORM_RESPONSE': {
      // Read latest responses from IDB — UPLOAD_FILE may have updated them with real file IDs
      // But keep the ORIGINAL currentVersion from payload (IDB version is locally incremented)
      const freshTask = yield call(() => db.tasks.get(payload.taskId))
      const freshResponses = freshTask?.formResponse?.responses || payload.responses
      const syncPayload = { ...payload, responses: freshResponses }
      const result = yield call(taskService.saveFormResponse, syncPayload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      break
    }
    case 'UPLOAD_FILE': {
      yield call(processFileUpload, entry)
      break
    }
    case 'DELETE_FORM_FILE': {
      yield call(taskService.deleteFormFile, { taskId: payload.taskId, fileId: payload.fileId })
      break
    }
    default:
      console.warn('Unknown sync entry type:', type)
  }
}

function* processFileUpload(entry) {
  const { payload } = entry
  const { offlineFileId } = payload

  // Read compressed blob from IDB
  const pendingFile = yield call(
    () => db.pendingFiles.where('offlineFileId').equals(offlineFileId).first()
  )
  if (!pendingFile?.blob) {
    throw new Error('Pending file not found in offline storage: ' + offlineFileId)
  }

  // Get presigned upload URL
  const urlResult = yield call(taskService.generateFormFileUploadUrl, {
    taskId: payload.taskId,
    fieldKey: pendingFile.fieldKey,
    filename: pendingFile.fileName,
    contentType: pendingFile.contentType
  })

  const { uploadUrl, fileId } = urlResult.data

  // Upload blob to S3 via XHR
  yield call(() => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl, true)
    xhr.setRequestHeader('Content-Type', pendingFile.contentType)
    xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`))
    xhr.onerror = () => reject(new Error('Upload network error'))
    xhr.send(pendingFile.blob)
  }))

  // Confirm upload
  const confirmResult = yield call(taskService.confirmFormFileUpload, {
    taskId: payload.taskId,
    fileId,
    fieldKey: pendingFile.fieldKey
  })

  const fileData = confirmResult.data

  // Update the form response in IDB to replace offline placeholder with real file data
  const task = yield call(() => db.tasks.get(payload.taskId))
  if (task?.formResponse?.responses) {
    const responses = JSON.parse(JSON.stringify(task.formResponse.responses))
    const fieldKey = pendingFile.fieldKey

    const realFileValue = {
      file_id: fileData.id,
      file_name: fileData.name,
      description: fileData.description || '',
      url: fileData.url,
      size: fileData.size || 0
    }

    console.log('[SYNC] processFileUpload: looking for placeholder', { fieldKey, offlineFileId })

    // Brute-force: walk entire responses tree and replace any object that contains this offlineFileId
    const replaceInObj = (obj) => {
      if (!obj || typeof obj !== 'object') return false
      let replaced = false

      for (const key of Object.keys(obj)) {
        const val = obj[key]
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          // Check ALL keys of this object for the offlineFileId value
          const vals = Object.values(val)
          if (vals.includes(offlineFileId)) {
            console.log('[SYNC] Found placeholder at key:', key, val)
            obj[key] = realFileValue
            replaced = true
          }
        } else if (Array.isArray(val)) {
          val.forEach((entry, i) => {
            if (entry && typeof entry === 'object') {
              for (const fk of Object.keys(entry)) {
                const fv = entry[fk]
                if (fv && typeof fv === 'object' && !Array.isArray(fv)) {
                  const fvals = Object.values(fv)
                  if (fvals.includes(offlineFileId)) {
                    console.log('[SYNC] Found placeholder in array entry', key, i, fk, fv)
                    entry[fk] = realFileValue
                    replaced = true
                  }
                }
              }
            }
          })
        }
      }
      return replaced
    }

    const found = replaceInObj(responses)
    console.log('[SYNC] Replacement result:', found, 'fileData:', fileData)

    if (found) {
      yield call(() => db.tasks.update(payload.taskId, {
        formResponse: { ...task.formResponse, responses }
      }))
      console.log('[SYNC] IDB updated with real file data')
    } else {
      console.warn('[SYNC] Could not find placeholder in responses. Keys:', Object.keys(responses))
      // Dump the full responses for debugging
      console.warn('[SYNC] Full responses:', JSON.stringify(responses).substring(0, 500))
    }
  } else {
    console.warn('[SYNC] No task or responses found in IDB for', payload.taskId)
  }

  // Delete blob from IDB
  yield call(() => db.pendingFiles.where('offlineFileId').equals(offlineFileId).delete())
}

// Lightweight connectivity check — bypasses axios entirely
const checkConnectivity = () =>
  fetch('/api/tasks/stats', { method: 'HEAD', cache: 'no-store', credentials: 'include' })
    .then(res => res.ok)
    .catch(() => false)

// Trigger sync with delay for network stabilization
function* triggerSyncSaga() {
  yield delay(3000)
  if (!isOnline()) return

  // Verify actual connectivity — navigator.onLine lies on mobile
  const isReachable = yield call(checkConnectivity)
  if (!isReachable) return

  try {
    yield call(processSyncQueue)
  } catch (error) {
    console.error('Sync process failed:', error)
    yield put(syncFailRoutine.success())
    yield put(updateSyncCountsRoutine.trigger())
  }
}


// Check for pending sync entries on app startup (handles page refresh while online)
function* startupSyncCheck() {
  yield delay(3000)
  if (!isOnline()) return
  // Also refresh sync counts on startup
  yield call(refreshSyncCounts)
  const entries = yield call(getPendingSyncEntries)
  if (entries.length > 0) {
    yield put(triggerSyncRoutine())
  }
}

// Periodic sync retry: check every 30s for pending entries when online
function* periodicSyncRetry() {
  while (true) {
    yield delay(30000)
    if (!isOnline()) continue
    const isSyncing = yield select(state => state.offline.isSyncing)
    if (isSyncing) continue
    try {
      // Verify real connectivity before triggering sync
      const isReachable = yield call(checkConnectivity)
      if (!isReachable) continue
      const entries = yield call(getPendingSyncEntries)
      if (entries.length > 0) {
        yield put(triggerSyncRoutine())
      }
    } catch (e) {
      // Silently ignore — periodic check is non-critical
    }
  }
}

// --- Watchers ---

function* refreshSyncCountsWatcher() {
  yield takeLatest(updateSyncCountsRoutine.TRIGGER, refreshSyncCounts)
}

function* onlineStatusWatcher() {
  yield takeLatest(setOnlineStatusRoutine.TRIGGER, handleOnlineStatusChange)
}

function* triggerSyncWatcher() {
  yield takeLatest(triggerSyncRoutine.TRIGGER, triggerSyncSaga)
}

export const offlineSagas = [
  fork(startupSyncCheck),
  fork(periodicSyncRetry),
  fork(onlineStatusWatcher),
  fork(refreshSyncCountsWatcher),
  fork(triggerSyncWatcher)
]
